<script setup lang="ts">
/**
 * Radar Legend Component
 * Shows precipitation type bars: Rain, Frz Rain, Mix, Snow.
 * Always displays all types since weather.com tiles include all precip colors.
 */
import { Droplets, CloudRain, Snowflake, CloudHail } from "@lucide/vue";

// Rain: green → yellow → orange → red → pink
const rainColors = [
  "#50c850",
  "#32b432",
  "#1ea01e",
  "#148c14",
  "#b4c81e",
  "#f0f000",
  "#ffc800",
  "#ff9600",
  "#ff6400",
  "#ff321e",
  "#f01432",
  "#d2145a",
  "#be3282",
  "#dc82b4",
];

// Freezing rain: purple/violet tones
const freezingRainColors = [
  "#3c1478",
  "#502391",
  "#6432aa",
  "#7846be",
  "#8c5ad2",
  "#a073e1",
  "#b48ceb",
  "#c3a5f5",
  "#d2b9fa",
  "#e1cdfc",
  "#eee1fe",
  "#f5eeff",
  "#faf8ff",
  "#ffffff",
];

// Mix: dark magenta → light pink
const mixColors = [
  "#8c1e64",
  "#a52d73",
  "#b94187",
  "#cd5a9b",
  "#dc73af",
  "#eb91c3",
  "#f5afd7",
  "#fac8e6",
  "#fcdcf2",
  "#feebfa",
  "#fff5fd",
  "#fffafe",
  "#fffdff",
  "#ffffff",
];

// Snow: blue tones
const snowColors = [
  "#1e50a0",
  "#2864b4",
  "#3778c8",
  "#468cd7",
  "#5aa0e6",
  "#6eb4f0",
  "#87c3f8",
  "#a0d2fc",
  "#b4e1ff",
  "#c8eeff",
  "#dcf5ff",
  "#ebfaff",
  "#f5fcff",
  "#ffffff",
];

const legendEntries = [
  { label: "Rain", colors: rainColors, icon: Droplets },
  { label: "Frz Rain", colors: freezingRainColors, icon: CloudHail },
  { label: "Mix", colors: mixColors, icon: CloudRain },
  { label: "Snow", colors: snowColors, icon: Snowflake },
];

/** Build a continuous legend gradient from the ordered colors. */
function buildGradient(colors: string[]): string {
  const stops = colors.map((color, i) => {
    const percent = (i / (colors.length - 1)) * 100;
    return `${color} ${percent}%`;
  });
  return `linear-gradient(to right, ${stops.join(", ")})`;
}
</script>

<template>
  <div
    class="flex w-fit items-center gap-2 rounded-panel border border-app-border bg-app-material px-3 py-2"
  >
    <div
      v-for="entry in legendEntries"
      :key="entry.label"
      class="flex items-center gap-1"
    >
      <component :is="entry.icon" class="h-3.5 w-3.5 text-text-primary" />
      <span
        class="text-[11px] font-medium whitespace-nowrap text-text-primary"
        >{{ entry.label }}</span
      >
      <div
        class="h-2.5 w-10 rounded-[5px]"
        :style="{ background: buildGradient(entry.colors) }"
      />
    </div>
  </div>
</template>
